import { queryClientGetBankByBic, queryClientCheckInn } from '@/shared/api'
import {
    zEmptyString,
    zodBankAccount,
    zodBic,
    zodUniversalInn,
    zodPhone,
    zodPassport,
} from '@/shared/lib/zod'
import { zNonEmptyString } from '@synapex/tools/utils'
import { Bank } from '@synapex/prisma/client'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import { DadataCompanyData } from '@synapex/shared2/gateway-rest-api'
import { type PickByValue } from '@synapex/tools/typescript'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback, useMemo, useState } from 'react'
import { z } from 'zod'

const TEXT = {
    requiredField: 'Обязательное поле',
    inn: {
        invalid_characters: 'ИНН должен состоять из цифр',
        invalid_length: 'ИНН должен состоять из 10 или 12 цифр',
        invalid_checksum: 'Неверное значение ИНН',
    },
    account: {
        invalid_characters: 'Рассчетный счет должен состоять из цифр',
        invalid_length_20: 'Рассчетный счет должен состоять из 20 цифр',
    },
    bic: {
        invalid_characters: 'БИК должен состоять из цифр',
        invalid_length_9: 'БИК должен состоять из 9 цифр',
    },
    ogrn: {
        invalid_characters: 'ОГРН должен состоять из цифр',
        invalid_length_13: 'ОГРН должен состоять из 13 цифр',
        invalid_length_15: 'ОГРНИП должен состоять из 15 цифр',
    },
    name: {
        physical: {
            invalidChars:
                'Допустимы только русские буквы, пробел, тире, кавычка и римские числа',
            invalidStartEnd:
                'Наименование не может начинаться или заканчиваться пробелом, тире или кавычкой',
            consecutiveSeparators: 'Разделители не могут идти подряд',
        },
        ip: {
            invalidChars:
                'Допустимы только русские буквы, пробел, точка, тире, кавычка и римские числа',
            invalidStart:
                'Наименование не может начинаться пробелом, тире, точкой или кавычкой',
            invalidEnd:
                'Наименование не может заканчиваться пробелом, тире или кавычкой',
            consecutiveSeparators:
                'Разделители не могут идти подряд (кроме точки перед пробелом)',
        },
        juridical: {
            invalidChars:
                'Допустимы только русские и латинские буквы, цифры, пробел и символы: - – — & # , . ; ! ? \' ` + * № / : | _ % ° « » " ( )',
            invalidStartEnd:
                'Наименование не может начинаться или заканчиваться пробелом',
            onlySymbols: 'Наименование не может состоять только из символов',
        },
    },
}

// Тире: обычное, среднее, длинное
const DASHES = ['-', '–', '—']
const DASHES_PATTERN = `[${DASHES.join('')}]`

// Валидатор имени для физ. лица (physical)
const zodNamePhysical = (base: z.ZodString) =>
    base
        .refine(
            (val) => {
                // Допустимы: русские буквы, пробел, тире (- – —), кавычка ('), римские числа (I V X L C D M)
                const pattern = /^[А-Яа-яЁё\s\-–—'IVXLCDM]+$/
                return pattern.test(val)
            },
            { message: TEXT.name.physical.invalidChars }
        )
        .refine(
            (val) => {
                // Не может начинаться/заканчиваться пробелом, тире или кавычкой
                const invalidStartEnd = /^[\s\-–—']|[\s\-–—']$/
                return !invalidStartEnd.test(val)
            },
            { message: TEXT.name.physical.invalidStartEnd }
        )
        .refine(
            (val) => {
                // Не более одного пробела между словами
                if (/\s{2,}/.test(val)) return false
                // Не более одного тире между словами
                const dashPattern = new RegExp(`${DASHES_PATTERN}{2,}`)
                if (dashPattern.test(val)) return false
                // Не более одной кавычки между словами
                if (/'{2,}/.test(val)) return false
                // Разделители не могут идти подряд (пробел, тире, кавычка в любой комбинации)
                const separatorPattern = new RegExp(
                    `[\\s${DASHES.join('')}'][\\s${DASHES.join('')}']`
                )
                if (separatorPattern.test(val)) return false
                return true
            },
            { message: TEXT.name.physical.consecutiveSeparators }
        )

// Валидатор имени для ИП (ip)
const zodNameIp = (base: z.ZodString) =>
    base
        .refine(
            (val) => {
                // Допустимы: русские буквы, пробел, точка, тире (- – —), кавычка ('), римские числа
                const pattern = /^[А-Яа-яЁё\s.\-–—'IVXLCDM]+$/
                return pattern.test(val)
            },
            { message: TEXT.name.ip.invalidChars }
        )
        .refine(
            (val) => {
                // Не может начинаться пробелом, тире, точкой или кавычкой
                const invalidStart = /^[\s.\-–—']/
                return !invalidStart.test(val)
            },
            { message: TEXT.name.ip.invalidStart }
        )
        .refine(
            (val) => {
                // Не может заканчиваться пробелом, тире или кавычкой (точка допустима)
                const invalidEnd = /[\s\-–—']$/
                return !invalidEnd.test(val)
            },
            { message: TEXT.name.ip.invalidEnd }
        )
        .refine(
            (val) => {
                // Не более одного пробела между словами
                if (/\s{2,}/.test(val)) return false
                // Не более одной точки между словами
                if (/\.{2,}/.test(val)) return false
                // Не более одного тире между словами
                const dashPattern = new RegExp(`${DASHES_PATTERN}{2,}`)
                if (dashPattern.test(val)) return false
                // Не более одной кавычки между словами
                if (/'{2,}/.test(val)) return false
                // Разделители не могут идти подряд, кроме точки перед пробелом
                // Проверяем комбинации разделителей (исключая ". ")
                const separators = `\\s${DASHES.join('')}'`
                // Любой разделитель (кроме точки) + любой разделитель
                const pattern1 = new RegExp(`[${separators}][${separators}.]`)
                // Точка + любой разделитель кроме пробела
                const pattern2 = new RegExp(`\\.[${DASHES.join('')}']`)
                if (pattern1.test(val) || pattern2.test(val)) return false
                return true
            },
            { message: TEXT.name.ip.consecutiveSeparators }
        )

// Валидатор имени для юр. лица (juridical)
const zodNameJuridical = (base: z.ZodString) =>
    base
        .refine(
            (val) => {
                // Допустимы: русские и латинские буквы, цифры, пробел и символы
                const pattern =
                    /^[А-Яа-яЁёA-Za-z0-9\s\-–—&#,.;!?'`+*№/:|_%°«»"()]+$/
                return pattern.test(val)
            },
            { message: TEXT.name.juridical.invalidChars }
        )
        .refine(
            (val) => {
                // Не может начинаться/заканчиваться пробелом
                return !val.startsWith(' ') && !val.endsWith(' ')
            },
            { message: TEXT.name.juridical.invalidStartEnd }
        )
        .refine(
            (val) => {
                // Не может состоять только из символов (без букв и цифр)
                const hasLetterOrDigit = /[А-Яа-яЁёA-Za-z0-9]/
                return hasLetterOrDigit.test(val)
            },
            { message: TEXT.name.juridical.onlySymbols }
        )

// Функция для получения валидатора имени по типу
export const getNameValidator = (
    type: ContragentType
): ((base: z.ZodString) => z.ZodType<string>) => {
    switch (type) {
        case 'physical':
        case 'self_employed':
            return zodNamePhysical
        case 'ip':
            return zodNameIp
        case 'juridical':
        case 'autonomous_non_profit_organization':
            return zodNameJuridical
    }
}

export const contragentTypeValues = [
    'physical',
    'ip',
    'juridical',
    'self_employed',
    'autonomous_non_profit_organization',
] as const
export type ContragentType = (typeof contragentTypeValues)[number]
export type ContragentIndividualType = Exclude<
    ContragentType,
    'juridical' | 'autonomous_non_profit_organization'
>

const requiredString = zNonEmptyString(TEXT.requiredField)

const requiredPhone = zodPhone(
    'Введите телефон'
)

// Валидатор ОГРН для юр. лиц (13 цифр)
const zodOgrnJuridical = (base: z.ZodString) =>
    base.refine((val) => /^\d{13}$/.test(val), {
        message: TEXT.ogrn.invalid_length_13,
    })

// Валидатор ОГРН для ИП (15 цифр)
const zodOgrnIp = (base: z.ZodString) =>
    base.refine((val) => /^\d{15}$/.test(val), {
        message: TEXT.ogrn.invalid_length_15,
    })

// Общий валидатор ИНН для обеих схем (10 или 12 цифр)
const universalInn = zodUniversalInn(requiredString, TEXT.inn)
const email = z.email({ error: 'Введите корректный email' })

// Схема для юр. лица (ИНН 10 цифр)
const publisherFormCompanySchema = z.object({
    name: requiredString,
    inn: universalInn,
    bic: zodBic(requiredString, false, TEXT.bic),
    kpp: requiredString,
    ogrn: zodOgrnJuridical(requiredString),
    currentAccount: zodBankAccount(requiredString, TEXT.account),
    position: requiredString,
    fullName: requiredString,
    phone: requiredString.and(requiredPhone),
    email: requiredString.and(email),
    passport: z.string(), // Не используется для юр. лиц
    registrationAddress: z.string(), // Не используется для юр. лиц

    legalAddress: requiredString,
    actualAddress: requiredString,
})

// Схема для физ. лица (ИНН 12 цифр)
const publisherFormIndividualSchema = z.object({
    name: requiredString,
    inn: universalInn,
    bic: z.union([zodBic(requiredString, false, TEXT.bic), zEmptyString()]),
    kpp: z.string(),
    ogrn: z.union([zodOgrnIp(requiredString), zEmptyString()]),
    currentAccount: zodBankAccount(z.string().max(20), TEXT.account, true),
    // Необязательные для физ. лиц
    position: z.string(),
    fullName: z.string(),
    phone: z.union([requiredPhone, zEmptyString()]),
    email: z.union([email, zEmptyString()]),
    passport: z.union([
        zodPassport('Введите серию и номер паспорта в формате 1234 567890'),
        zEmptyString(),
    ]),
    registrationAddress: z.string(),

    legalAddress: z.string(),
    actualAddress: z.string(),
})

export type FormValues = z.input<typeof publisherFormCompanySchema> &
    z.input<typeof publisherFormIndividualSchema>

export type TextFieldKey = keyof PickByValue<FormValues, string>

export const getFieldMaxLength = (
    type: ContragentType,
    name: keyof PickByValue<FormValues, string>
) => {
    // ИНН теперь может быть 10 или 12 цифр
    if (name === 'inn') {
        return 12
    }

    // ОГРН для юр. лиц - 13 цифр, для ИП - 15 цифр
    if (name === 'ogrn') {
        return type === 'ip' ? 15 : 13
    }

    const schema =
        type === 'juridical' || type === 'autonomous_non_profit_organization'
            ? publisherFormCompanySchema
            : publisherFormIndividualSchema

    const field = schema.shape[name]

    if (field instanceof z.ZodUnion) {
        return field.options[0].maxLength ?? undefined
    } else if (field instanceof z.ZodIntersection) {
        return undefined
    } else {
        return field.maxLength ?? undefined
    }
}

export const isFieldRequired = (
    type: ContragentType,
    name: keyof PickByValue<FormValues, string>,
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
) => {
    if (type === 'juridical' || type === 'autonomous_non_profit_organization') {
        return true
    }

    // // ОГРН обязателен для ИП
    // if (type === 'ip' && name === 'ogrn') {
    //     return true
    // }

    // Для advertiser, agency, ors все поля обязательны
    if (
        roleType === 'advertiser' ||
        roleType === 'agency' ||
        roleType === 'ors'
    ) {
        return true
    }

    // Для publisher только inn и name обязательны
    return name === 'inn' || name === 'name'
}

export function useSchemaWithBic(
    type: ContragentType,
    defaultValues: FormValues,
    { workspace_type, workspace_id }: WorkspaceDescriptor,
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
) {
    const queryClient = useQueryClient()

    const [bankInfo, setBankInfo] = useState<Bank | undefined | null>(undefined)
    const [dadataInfo, setDadataInfo] = useState<DadataCompanyData | null>(null)
    const [innChecked, setInnChecked] = useState<boolean>(false)
    // Тип ИНН определённый по длине (10 = juridical, 12 = physical)
    const [innVariant, setInnVariant] = useState<
        'physical' | 'juridical' | null
    >(null)

    const checkBank = useCallback(
        async (ctx: z.core.ParsePayload<string>) => {
            let result: Bank | undefined | null = undefined

            // console.log(`queryClientGetBankByBic: ${ctx.value}`)

            if (ctx.issues.length > 0) {
                setBankInfo(null)
                return
            }

            try {
                result = await queryClientGetBankByBic(queryClient, ctx.value)
                // eslint-disable-next-line no-empty
            } catch {}

            if (result) {
                setBankInfo(result)
            } else {
                ctx.issues.push({
                    code: 'custom',
                    input: ctx.value,
                    continue: false,
                    message: 'Банк с таким БИК не найден',
                })
            }
        },
        [queryClient]
    )

    const checkInn = useCallback(
        async (ctx: z.core.ParsePayload<string>) => {
            if (defaultValues.inn === ctx.value) {
                setDadataInfo(null)
                return
            }

            // Определяем вариант ИНН по длине (10 = юр.лицо, 12 = физ.лицо)
            const variant = ctx.value.length === 10 ? 'juridical' : 'physical'

            try {
                const result = await queryClientCheckInn(
                    queryClient,
                    { workspace_type, workspace_id },
                    ctx.value
                )

                if (!result.available) {
                    setInnChecked(false)
                    setInnVariant(null)
                    ctx.issues.push({
                        code: 'custom',
                        input: ctx.value,
                        continue: false,
                        message: 'Контрагент с таким ИНН уже существует',
                    })
                    setDadataInfo(null)
                } else if (result.name && result.inn && result.type) {
                    const dadataData: DadataCompanyData = {
                        inn: result.inn,
                        kpp: result.kpp ?? null,
                        ogrn: result.ogrn ?? null,
                        ogrnDate: result.ogrnDate ?? null,
                        type: result.type,
                        name: result.name,
                        address: result.address ?? null,
                        management: result.management ?? null,
                        email: result.email ?? null,
                        phone: result.phone ?? null,
                    }
                    setInnChecked(true)
                    setInnVariant(variant)
                    setDadataInfo(dadataData)
                } else {
                    setInnChecked(true)
                    setInnVariant(variant)
                    setDadataInfo(null)
                }
            } catch (error) {
                if (isAxiosError(error)) {
                    if (error.response?.status === 404) {
                        ctx.issues.push({
                            code: 'custom',
                            input: ctx.value,
                            continue: false,
                            message: 'ИНН не найден',
                        })
                    } else {
                        ctx.issues.push({
                            code: 'custom',
                            input: ctx.value,
                            continue: false,
                            message:
                                'Не удалось проверить ИНН. Попробуйте позже или введите данные вручную',
                        })
                    }
                }

                setInnChecked(false)
                setInnVariant(null)
                setDadataInfo(null)
            }
        },
        [queryClient, workspace_type, workspace_id, defaultValues.inn]
    )

    const schema = useMemo(() => {
        const isJuridical =
            type === 'juridical' ||
            type === 'autonomous_non_profit_organization'

        const nameValidator = getNameValidator(type)
        const isRequiredRole =
            roleType === 'advertiser' ||
            roleType === 'agency' ||
            roleType === 'ors'

        if (isJuridical) {
            // Схема для юр. лица
            const shape = {
                ...publisherFormCompanySchema.shape,
                name: nameValidator(requiredString),
                inn: publisherFormCompanySchema.shape.inn.check(checkInn),
                bic: publisherFormCompanySchema.shape.bic.check(checkBank),
                ogrn: zodOgrnJuridical(requiredString),
            }

            return z.object(shape).superRefine((data, ctx) => {
                if (data.bic && !data.currentAccount) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Расчетный счет обязателен при указании БИК',
                        path: ['currentAccount'],
                    })
                }
            })
        } else {
            // Схема для физ. лица
            const shape = {
                ...publisherFormIndividualSchema.shape,
                name: nameValidator(requiredString),
                inn: publisherFormIndividualSchema.shape.inn.check(checkInn),
                bic: isRequiredRole
                    ? zodBic(requiredString, false, TEXT.bic).check(checkBank)
                    : z.union([
                          publisherFormIndividualSchema.shape.bic.options[0].check(
                              checkBank
                          ),
                          publisherFormIndividualSchema.shape.bic.options[1],
                      ]),
                ogrn:
                    type === 'ip'
                        ? zodOgrnIp(requiredString)
                        : z.union([z.string(), zEmptyString()]),
                currentAccount: isRequiredRole
                    ? zodBankAccount(requiredString, TEXT.account)
                    : publisherFormIndividualSchema.shape.currentAccount,
                phone: isRequiredRole
                    ? requiredString.and(requiredPhone)
                    : publisherFormIndividualSchema.shape.phone,
                email: isRequiredRole
                    ? requiredString.and(email)
                    : publisherFormIndividualSchema.shape.email,
                passport:
                    isRequiredRole && type !== 'ip'
                        ? zodPassport(
                              'Введите серию и номер паспорта в формате 1234 567890'
                          )
                        : publisherFormIndividualSchema.shape.passport,
                registrationAddress: isRequiredRole
                    ? requiredString
                    : publisherFormIndividualSchema.shape.registrationAddress,
            }

            return z.object(shape).superRefine((data, ctx) => {
                // Проверка связи БИК и расчётного счёта
                if (data.bic) {
                    if (!data.currentAccount) {
                        ctx.addIssue({
                            code: 'custom',
                            message:
                                'Расчетный счет обязателен при указании БИК',
                            path: ['currentAccount'],
                        })
                    }
                } else {
                    if (data.currentAccount) {
                        ctx.addIssue({
                            code: 'custom',
                            message:
                                'БИК обязателен при указании расчетного счета',
                            path: ['bic'],
                        })
                    }
                }
            })
        }
    }, [type, checkBank, checkInn, roleType])

    return { schema, bankInfo, innChecked, dadataInfo, innVariant }
}
